import {startOfUtcWeek} from 'src/shared/utils/utcDate';
import type {TimelineMode} from '../InspectionList/inspectionTimelineGrouping';

export type DispatchView = TimelineMode;

const dayMs = 24 * 60 * 60 * 1000;

const startOfUtcDay = (date: Date): Date =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );

const startOfUtcMonth = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

export interface DateWindow {
  fromUtc: Date;
  toUtc: Date;
}

// Half-open [fromUtc, toUtc) window covering the anchor's day/week/month.
export const windowFor = (anchor: Date, view: DispatchView): DateWindow => {
  switch (view) {
    case 'day': {
      const from = startOfUtcDay(anchor);
      return {fromUtc: from, toUtc: new Date(from.getTime() + dayMs)};
    }
    case 'week': {
      const from = startOfUtcWeek(anchor);
      return {fromUtc: from, toUtc: new Date(from.getTime() + 7 * dayMs)};
    }
    case 'month': {
      const from = startOfUtcMonth(anchor);
      const to = new Date(
        Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1)
      );
      return {fromUtc: from, toUtc: to};
    }
  }
};

export const shiftAnchor = (
  anchor: Date,
  view: DispatchView,
  direction: -1 | 1
): Date => {
  const d = startOfUtcDay(anchor);
  switch (view) {
    case 'day':
      d.setUTCDate(d.getUTCDate() + direction);
      return d;
    case 'week':
      d.setUTCDate(d.getUTCDate() + 7 * direction);
      return d;
    case 'month':
      // Normalize to day 1 first — otherwise a day-31 anchor overflows
      // (e.g. Jan 31 + 1 month → Mar 2/3).
      d.setUTCDate(1);
      d.setUTCMonth(d.getUTCMonth() + direction);
      return d;
  }
};

export const DAY_START_HOUR = 7;
export const DAY_END_HOUR = 18;

// Week/month cells have no hour lane; dropped cards default to this hour.
export const WEEK_DEFAULT_HOUR = 8;

export const dayHours = (): number[] => {
  const hours: number[] = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) hours.push(h);
  return hours;
};

export const weekDays = (anchor: Date): Date[] => {
  const start = startOfUtcWeek(anchor);
  return Array.from({length: 7}, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(d.getUTCDate() + i);
    return d;
  });
};

export const monthDays = (anchor: Date): Date[] => {
  const start = startOfUtcMonth(anchor);
  const days: Date[] = [];
  const month = start.getUTCMonth();
  const cursor = new Date(start);
  while (cursor.getUTCMonth() === month) {
    days.push(new Date(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
};

// Monday-anchored weekday headers matching the week view's ordering.
export const monthWeekdayLabels = (): string[] => {
  const monday = startOfUtcWeek(new Date(Date.UTC(2024, 0, 1)));
  return Array.from({length: 7}, (_, i) => {
    const d = new Date(monday);
    d.setUTCDate(d.getUTCDate() + i);
    return d.toLocaleDateString(undefined, {weekday: 'short', timeZone: 'UTC'});
  });
};

// Month grid cells, Monday-first, with null padding so day 1 sits in its true
// weekday column and the last week is filled out.
export const monthCalendarCells = (anchor: Date): (Date | null)[] => {
  const days = monthDays(anchor);
  const leading = (days[0].getUTCDay() + 6) % 7;
  const cells: (Date | null)[] = Array.from({length: leading}, () => null);
  cells.push(...days);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

// Builds a UTC-naive Date on the given calendar day at the given hour/minute.
export const atHour = (day: Date, hour: number, minute = 0): Date =>
  new Date(
    Date.UTC(
      day.getUTCFullYear(),
      day.getUTCMonth(),
      day.getUTCDate(),
      hour,
      minute
    )
  );

export const rangeLabel = (anchor: Date, view: DispatchView): string => {
  const opts: Intl.DateTimeFormatOptions =
    view === 'month'
      ? {month: 'long', year: 'numeric', timeZone: 'UTC'}
      : {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'};
  if (view === 'week') {
    const days = weekDays(anchor);
    const start = days[0].toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    const end = days[6].toLocaleDateString(undefined, opts);
    return `${start} – ${end}`;
  }
  return anchor.toLocaleDateString(undefined, opts);
};
